import { useGetFieldSiteMembersQuery, useListPtoMembersQuery, useMeQuery } from '@shared/api';
import type { UserContext } from '@entities/user';
import { fieldAccessOf, isFieldReviewer, isSiteForeman, type FieldAccess } from './types';

/** Право записи агента у текущего пользователя (как `require_agent_access`: чтение + запись). */
function canWriteAgent(me: UserContext | undefined, agentCode: string): boolean {
  return !!me?.agents.some((a) => a.agent_code === agentCode && a.can_read && a.can_write);
}

/**
 * Права текущего пользователя в Прорабе по проекту ПТО (площадке этого проекта):
 * проверяющий ПТО / ведёт справочники / сквозной доступ — см. [`fieldAccessOf`].
 * Участие в команде проекта читаем из `/agents/pto/projects/{id}/members` и только
 * когда глобальной роли проверяющего нет. Пока `/auth/me` или проект грузятся —
 * всё false: лучше спрятать кнопку, чем показать её и получить 403.
 */
export function useFieldAccess(projectId: string | null | undefined): FieldAccess {
  const { data: me } = useMeQuery();
  const fieldCanWrite = canWriteAgent(me, 'field');
  const ptoCanWrite = canWriteAgent(me, 'pto');
  const needMembership =
    !!projectId && !!me && fieldCanWrite && ptoCanWrite && !isFieldReviewer(me.roles);
  const { data: members } = useListPtoMembersQuery(projectId ?? '', { skip: !needMembership });
  const ptoMemberRole = needMembership
    ? (members?.items.find((m) => m.user_id === me?.user.id)?.role ?? null)
    : null;
  return fieldAccessOf({ roles: me?.roles, fieldCanWrite, ptoCanWrite, ptoMemberRole });
}

/** id текущего пользователя (`/auth/me`); до загрузки — undefined. Нужен там,
 *  где сервер различает «своё»: свой рапорт ПТО не принимает, свою правку прораб
 *  не подтверждает (КR11). */
export function useCurrentUserId(): string | undefined {
  const { data: me } = useMeQuery();
  return me?.user.id;
}

/**
 * Текущий пользователь — прораб этой площадки (участник `foreman` | `site_chief`
 * с правом записи Прораба). Членство читаем из `/sites/{id}/members`; без
 * площадки или до загрузки — false.
 */
export function useSiteForeman(siteId: string | null | undefined): boolean {
  const { data: me } = useMeQuery();
  const fieldCanWrite = canWriteAgent(me, 'field');
  const { data } = useGetFieldSiteMembersQuery(siteId ?? '', {
    skip: !siteId || !fieldCanWrite,
  });
  return fieldCanWrite && isSiteForeman(data?.members, me?.user.id);
}
